import { wait } from '@gem/utils';

import { isJSONResponse } from '../RestClient/RestClient.utils';
import { RestClientHttpError } from '../RestClientHttpError/RestClientHttpError';
import { HttpStatusCode } from '../constants';
import {
  AWAIT_FIRST_RETRY,
  AWAIT_RATE_LIMIT_RETRY,
  AWAIT_SECOND_RETRY,
  RETRY_ERROR_CODES,
  ROOT_BASE_URL,
} from './RestClientRequestController.constants';
import {
  IRestClientRequestOptions,
  IRestClientRequestParams,
  IRestClientResponse,
} from './RestClientRequestController.types';

export class RestClientRequestController {
  headers: Record<string, string | null>;
  abortController: AbortController;

  constructor(params: IRestClientRequestParams) {
    this.headers = {
      'Content-Type': 'application/json',
      ...params.headers,
    };

    this.abortController = new AbortController();
  }

  async makeRequest<D = Record<string, any>, B = Record<string, any>>(
    endpoint: string,
    options: IRestClientRequestOptions<B>,
    attempt = 0
  ): Promise<IRestClientResponse<D>> {
    const { method, useRawResData, useRawReqData, credentials, baseUrl = '' } = options;

    const headers = Object.entries({
      ...this.headers,
      ...(options.headers || {}),
    }).reduce<Record<string, string>>((acc, [key, value]) => {
      if (value === null) {
        return acc;
      }
      acc[key] = value;
      return acc;
    }, {});

    if (options.headers?.['Content-Type'] === null) {
      delete headers['Content-Type'];
    }

    const body = options.body ? JSON.stringify(options.body) : null;
    const url =
      baseUrl === ROOT_BASE_URL
        ? `${baseUrl}${endpoint}`
        : [baseUrl, endpoint].filter(Boolean).join('/');

    const requestInfo = new Request(url, {
      method,
      body: useRawReqData ? (options.body as ArrayBuffer) : body,
      headers: new Headers(headers),
      signal: this.abortController.signal,
      credentials,
    });

    try {
      const response = await fetch(requestInfo);
      return this.handleResponse<D, B>(response, options, endpoint, useRawResData);
    } catch (error) {
      return this.handleError<D, B>(error, endpoint, options, attempt).catch((error) => {
        throw error;
      });
    }
  }

  async createResponseData<D>(response: Response, useRawResData?: boolean): Promise<D> {
    const data: D =
      useRawResData || !isJSONResponse(response)
        ? await response.arrayBuffer()
        : await response.json();
    return data;
  }

  async handleResponse<D, B>(
    response: Response,
    requestOptions: IRestClientRequestOptions<B>,
    requestEndpoint: string,
    useRawResData?: boolean
  ): Promise<IRestClientResponse<D>> {
    if (!response.ok) {
      const errorResponse = await response.text();
      throw new RestClientHttpError(
        response.status,
        response.statusText,
        requestEndpoint,
        requestOptions,
        errorResponse,
        response.headers
      );
    }
    const responseData = await this.createResponseData<D>(response, useRawResData);
    return {
      data: responseData,
      headers: response.headers,
    };
  }

  async handleError<D, B>(
    error: any,
    requestEndpoint: string,
    requestOptions: IRestClientRequestOptions<B>,
    attempt: number
  ): Promise<IRestClientResponse<D>> {
    if (!(error instanceof RestClientHttpError)) {
      const awaitTime = [AWAIT_FIRST_RETRY, AWAIT_SECOND_RETRY][attempt];
      if (awaitTime === undefined) {
        throw new RestClientHttpError(
          HttpStatusCode.Unknown,
          'Unknown Error',
          requestEndpoint,
          requestOptions,
          JSON.stringify(error)
        );
      }
      await wait(awaitTime);
      return this.makeRequest(requestEndpoint, requestOptions, attempt + 1);
    }

    if (error.statusCode === HttpStatusCode.Unauthorized) {
      this.abortAllRequests();
    }

    if (error.statusCode === HttpStatusCode.TooManyRequests) {
      let retryTime = AWAIT_RATE_LIMIT_RETRY;

      if (error.responseHeaders) {
        retryTime = (Number(error.responseHeaders.get('X-RateLimit-Reset')) || 1) * 1000;
      }

      await wait(retryTime);
      return this.makeRequest(requestEndpoint, requestOptions);
    }

    if (RETRY_ERROR_CODES.includes(error.statusCode) && attempt === 0) {
      await wait(AWAIT_FIRST_RETRY);
      return this.makeRequest(requestEndpoint, requestOptions, attempt + 1);
    }

    throw error;
  }

  abortAllRequests(): void {
    this.abortController.abort();
    this.abortController = new AbortController();
  }
}
